import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const COLLECTIONS = [
  { slug: "the-doodles-official-collection", label: "The Doodles Official Collection" },
  { slug: "military-official-collection", label: "Military Official Collection" },
  { slug: "one-united-official-collection", label: "One United Official Collection" },
  { slug: "childrens-official-collection", label: "Children\u2019s Official Collection" },
  { slug: "seniors-collection", label: "Seniors Collection" },
  { slug: "the-encore-official-collection", label: "The Encore Official Collection" },
  { slug: "graphics-official-collection", label: "Graphics Official Collection" },
  { slug: "sports-official-collection", label: "Sports Official Collection" },
  { slug: "spine-official-collection", label: "Spine Official Collection" },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", collections: ["graphics-official-collection"] });
  const [designs, setDesigns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copyJob, setCopyJob] = useState(null);
  const [banner, setBanner] = useState({ enabled: false, message: "GRAND OPENING — Free shipping on orders $75+", cta_label: "SHOP NOW", cta_href: "/collections/graphics-official-collection", variant: "royal" });
  const [bannerSaving, setBannerSaving] = useState(false);
  const [recoveryBusy, setRecoveryBusy] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) nav("/auth");
  }, [user, loading, nav]);
  useEffect(() => { if (user?.is_admin) refresh(); }, [user]);

  // Load current banner config
  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/site/banner").then(r => setBanner(prev => ({ ...prev, ...r.data }))).catch(() => {});
  }, [user]);

  // Load latest batch-copy job status + poll if running
  useEffect(() => {
    if (!user?.is_admin) return;
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/admin/products/regenerate-copy-job");
        if (!cancel && r.data?.job_id) setCopyJob(r.data);
      } catch {}
    })();
    return () => { cancel = true; };
  }, [user]);

  useEffect(() => {
    if (!copyJob || copyJob.status === "complete") return;
    const id = setInterval(async () => {
      try {
        const r = await api.get(`/admin/products/regenerate-copy-job/${copyJob.job_id}`);
        setCopyJob(r.data);
        if (r.data.status === "complete") clearInterval(id);
      } catch {}
    }, 2500);
    return () => clearInterval(id);
  }, [copyJob?.job_id, copyJob?.status]);

  const startBatchCopyRegen = async () => {
    if (!window.confirm("Rewrite every product's description in the Hampton Blu brand voice? This runs in the background and costs about $0.0002 per product.")) return;
    try {
      const r = await api.post("/admin/products/regenerate-copy-all");
      setCopyJob({ job_id: r.data.job_id, total: r.data.queued, done: 0, failed: 0, status: "running" });
      toast.success(`Rewriting ${r.data.queued} descriptions in the background`);
    } catch { toast.error("Failed to start batch"); }
  };
  const refresh = async () => {
    try { const r = await api.get("/admin/designs"); setDesigns(r.data.designs); } catch {}
  };

  const saveBanner = async () => {
    setBannerSaving(true);
    try {
      const r = await api.patch("/admin/site/banner", banner);
      setBanner(prev => ({ ...prev, ...r.data }));
      toast.success(banner.enabled ? "Launch banner is LIVE on the site" : "Banner hidden from site");
    } catch { toast.error("Could not save banner"); }
    finally { setBannerSaving(false); }
  };

  const triggerRecoverySweep = async () => {
    setRecoveryBusy(true);
    try {
      const r = await api.post("/admin/cron/abandoned-cart/run-now");
      toast.success(`Scanned ${r.data.scanned} · emailed ${r.data.sent} · already-paid ${r.data.skipped_paid}`);
    } catch { toast.error("Sweep failed"); }
    finally { setRecoveryBusy(false); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete "${name}" and all its products? This cannot be undone.`)) return;
    try { const r = await api.delete(`/admin/designs/${id}`); toast.success(`Deleted ${r.data.deleted} products`); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  const regen = async (id) => {
    try { const r = await api.post(`/admin/designs/${id}/regenerate`); toast.success("Regeneration started — refresh in ~30 seconds"); }
    catch { toast.error("Regenerate failed"); }
  };

  const toggleColl = (c) => {
    setForm(f => ({ ...f, collections: f.collections.includes(c) ? f.collections.filter(x=>x!==c) : [...f.collections, c] }));
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Pick a design PNG"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", form.name);
      fd.append("description", form.description);
      fd.append("collections", form.collections.join(","));
      const r = await api.post("/admin/designs", fd, { headers: {"Content-Type":"multipart/form-data"} });
      toast.success(`Created ${r.data.products_created} products!`);
      setForm({ name:"", description:"", collections:["graphics-official-collection"] });
      setFile(null);
      refresh();
    } catch (e) {
      toast.error("Upload failed");
    } finally { setBusy(false); }
  };

  if (!user?.is_admin) return null;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-5xl text-[color:var(--hb-navy)]">Admin · Design Upload</h1>
          <p className="text-sm text-neutral-600 mt-2">Upload a design PNG (transparent bg recommended) — we'll auto-generate mockups on every apparel × color combo.</p>
        </div>
        <a href="/admin/bulk" className="hb-btn-secondary" data-testid="link-bulk">Bulk Upload →</a>
        <a href="/admin/printify" className="hb-btn-secondary" data-testid="link-printify">Printify →</a>
        <a href="/admin/orders" className="hb-btn-secondary" data-testid="link-orders">Orders →</a>
        <a href="/admin/vault" className="hb-btn-secondary" data-testid="link-vault">Design Vault →</a>
        <a href="/admin/chat" className="hb-btn-secondary" data-testid="link-chat">Assistant →</a>
        <a href="/admin/knowledge" className="hb-btn-secondary" data-testid="link-knowledge">AI & Business Knowledge →</a>
        <a href="/admin/inquiries" className="hb-btn-secondary" data-testid="link-inquiries">Inquiries →</a>
        <a href="/admin/studio" className="hb-btn-secondary" data-testid="link-studio">Design Studio →</a>
        <a href="/admin/history" className="hb-btn-secondary" data-testid="link-history">Chat History →</a>
      </div>

      {/* Batch product-copy regeneration */}
      <div className="mt-6 border border-neutral-200 bg-white p-5" data-testid="batch-copy-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-2xl text-[color:var(--hb-navy)]">Rewrite Product Descriptions</h2>
            <p className="text-sm text-neutral-600 mt-1">Have the assistant rewrite every product's description in your brand voice, in one batch.</p>
          </div>
          <button
            onClick={startBatchCopyRegen}
            disabled={copyJob?.status === "running"}
            className="text-xs uppercase tracking-widest bg-[color:var(--hb-navy)] text-white px-4 py-2 disabled:opacity-50"
            data-testid="batch-copy-btn"
          >
            {copyJob?.status === "running" ? "Running…" : "Rewrite All"}
          </button>
        </div>
        {copyJob?.job_id && (
          <div className="mt-4" data-testid="batch-copy-progress">
            <div className="flex justify-between text-xs text-neutral-600 mb-1.5">
              <span>{copyJob.status === "complete" ? "Complete" : "Running"}</span>
              <span>{copyJob.done} / {copyJob.total}{copyJob.failed > 0 && <span className="text-red-600 ml-2">({copyJob.failed} failed)</span>}</span>
            </div>
            <div className="h-2 bg-neutral-100 border border-neutral-200 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${copyJob.status === "complete" ? "bg-green-500" : "bg-[color:var(--hb-royal)]"}`}
                style={{ width: `${copyJob.total > 0 ? Math.round((copyJob.done / copyJob.total) * 100) : 0}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Launch banner editor */}
      <div className="mt-6 border border-neutral-200 bg-white p-5" data-testid="launch-banner-card">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
          <div>
            <h2 className="font-display text-2xl text-[color:var(--hb-navy)]">Launch Banner</h2>
            <p className="text-sm text-neutral-600 mt-1">A dismissible announcement bar at the very top of every page. Flip it ON the moment Stripe is live.</p>
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer" data-testid="banner-enabled-toggle">
            <input type="checkbox" checked={!!banner.enabled} onChange={e => setBanner(b => ({ ...b, enabled: e.target.checked }))} />
            <span className={banner.enabled ? "text-green-700" : "text-neutral-500"}>{banner.enabled ? "LIVE on site" : "Hidden"}</span>
          </label>
        </div>
        <div className="grid lg:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-3">
          <input type="text" value={banner.message || ""} onChange={e => setBanner(b => ({ ...b, message: e.target.value }))}
                 placeholder="Message (keep it short, ALL CAPS reads best)"
                 className="border border-neutral-300 px-3 py-2 text-sm" data-testid="banner-message-input"/>
          <input type="text" value={banner.cta_label || ""} onChange={e => setBanner(b => ({ ...b, cta_label: e.target.value }))}
                 placeholder="Button label"
                 className="border border-neutral-300 px-3 py-2 text-sm" data-testid="banner-cta-label-input"/>
          <input type="text" value={banner.cta_href || ""} onChange={e => setBanner(b => ({ ...b, cta_href: e.target.value }))}
                 placeholder="Button link (e.g. /collections/graphics-official-collection)"
                 className="border border-neutral-300 px-3 py-2 text-sm" data-testid="banner-cta-href-input"/>
          <select value={banner.variant || "royal"} onChange={e => setBanner(b => ({ ...b, variant: e.target.value }))}
                  className="border border-neutral-300 px-3 py-2 text-sm" data-testid="banner-variant-select">
            <option value="royal">Royal Blue</option>
            <option value="gold">Gold</option>
            <option value="red">Red</option>
          </select>
          <button onClick={saveBanner} disabled={bannerSaving}
                  className="text-xs uppercase tracking-widest bg-[color:var(--hb-navy)] text-white px-4 py-2 disabled:opacity-50"
                  data-testid="banner-save-btn">
            {bannerSaving ? "Saving…" : "Save"}
          </button>
        </div>
        {/* Live preview strip */}
        <div className="mt-4 text-xs text-neutral-500 mb-1">Live preview</div>
        <div className="text-center text-xs tracking-widest uppercase font-semibold py-2.5 px-10"
             style={{
               background: banner.variant === "gold" ? "#D4A64A" : banner.variant === "red" ? "#B91C1C" : "#1D4ED8",
               color: banner.variant === "gold" ? "#0A1F44" : "#ffffff",
             }} data-testid="banner-preview">
          <span className="mr-3">{banner.message || "(empty message)"}</span>
          {banner.cta_label && <span className="underline underline-offset-4 font-bold">{banner.cta_label} →</span>}
        </div>
      </div>

      {/* Abandoned-cart recovery */}
      <div className="mt-6 border border-neutral-200 bg-white p-5" data-testid="recovery-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-2xl text-[color:var(--hb-navy)]">Abandoned-Cart Emails</h2>
            <p className="text-sm text-neutral-600 mt-1">A background job runs every 20 minutes and emails shoppers whose checkout stalled 1-48 hours ago (one email per session). Need to test it right now?</p>
          </div>
          <button onClick={triggerRecoverySweep} disabled={recoveryBusy}
                  className="text-xs uppercase tracking-widest bg-[color:var(--hb-royal)] text-white px-4 py-2 disabled:opacity-50"
                  data-testid="run-recovery-btn">
            {recoveryBusy ? "Scanning…" : "Run Sweep Now"}
          </button>
        </div>
      </div>

      <form onSubmit={upload} className="mt-10 grid lg:grid-cols-2 gap-8 border border-neutral-200 p-6 bg-white">
        <div>
          <label className="text-xs uppercase tracking-widest text-neutral-500">Design file (PNG)</label>
          <input type="file" accept="image/png,image/jpeg" onChange={e=>setFile(e.target.files[0])} className="mt-2 block" required data-testid="admin-file"/>
          {file && <p className="text-xs text-neutral-500 mt-2">{file.name} · {(file.size/1024).toFixed(0)} KB</p>}
        </div>
        <div className="space-y-4">
          <input required placeholder="Design name (e.g., Blu Doodle Pack)" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}
            className="w-full border-b border-neutral-300 py-2" data-testid="admin-name"/>
          <textarea rows="3" placeholder="Short description" value={form.description} onChange={e=>setForm({...form,description:e.target.value})}
            className="w-full border border-neutral-300 p-2 text-sm" data-testid="admin-desc"/>
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Official Collections</div>
            <div className="flex flex-wrap gap-2">
              {COLLECTIONS.map(c => (
                <button type="button" key={c.slug} onClick={()=>toggleColl(c.slug)}
                  className={`px-3 py-1 text-xs border ${form.collections.includes(c.slug)?"bg-[color:var(--hb-navy)] text-white border-[color:var(--hb-navy)]":"border-neutral-300"}`}
                  data-testid={`coll-toggle-${c.slug}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="lg:col-span-2">
          <button disabled={busy} className="hb-btn-primary" data-testid="admin-upload-btn">
            {busy ? "Generating…" : "Upload & Auto-Generate Products"}
          </button>
        </div>
      </form>

      <h2 className="font-display text-3xl text-[color:var(--hb-navy)] mt-12 mb-4">Designs</h2>
      <div className="border border-neutral-200 divide-y divide-neutral-200" data-testid="designs-list">
        {designs.map(d => (
          <div key={d.design_id} className="p-4 flex items-center justify-between gap-4">
            <div className="flex-1">
              <div className="font-medium">{d.name}</div>
              <div className="text-xs text-neutral-500">{d.collections?.join(" · ")} · {d.product_count || 0} products</div>
            </div>
            <div className="text-xs text-neutral-500">{new Date(d.created_at).toLocaleDateString()}</div>
            <button onClick={()=>regen(d.design_id)} className="text-xs uppercase tracking-widest border border-neutral-300 px-3 py-1 hover:border-[color:var(--hb-navy)]" data-testid={`regen-${d.design_id}`}>Regenerate</button>
            <button onClick={()=>del(d.design_id, d.name)} className="text-xs uppercase tracking-widest border border-[color:var(--hb-red)] text-[color:var(--hb-red)] px-3 py-1 hover:bg-[color:var(--hb-red)] hover:text-white" data-testid={`del-${d.design_id}`}>Delete</button>
          </div>
        ))}
        {designs.length === 0 && <div className="p-6 text-sm text-neutral-500 text-center">No designs yet.</div>}
      </div>
    </div>
  );
}
